import { useEffect, useRef } from "react";
import "./styles.scss";

export default function PDFRender({ pdf, zoom, onPageInView, loading }) {
    const containerRef = useRef(null);
    const renderedPagesRef = useRef(new Set());
    const loadingPagesRef = useRef(new Set());

    useEffect(() => {
        if (!pdf || !containerRef.current) return;
        let cancelled = false;
        let stableWidth = 0;

        // Reset tracking on pdf/zoom change
        renderedPagesRef.current.clear();
        loadingPagesRef.current.clear();

        const container = containerRef.current;
        const scrollContainer = container.closest('.material-container');

        // Observer for reporting which page is currently in view (e.g. for sidebar sync)
        const visibilityObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
                    onPageInView(parseInt(entry.target.getAttribute('data-page')));
                }
            });
        }, {
            root: scrollContainer,
            threshold: [0.1, 0.5]
        });

        // Observer for lazy loading pages before they enter the viewport
        const lazyObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const pageNum = parseInt(entry.target.getAttribute('data-page'));
                    if (!renderedPagesRef.current.has(pageNum) && !loadingPagesRef.current.has(pageNum)) {
                        renderPage(pageNum, entry.target);
                    }
                }
            });
        }, {
            root: scrollContainer,
            rootMargin: '1000px 0px', // Load pages 1000px before they enter view
            threshold: 0
        });

        async function renderPage(pageNum, pageHost) {
            if (loadingPagesRef.current.has(pageNum) || renderedPagesRef.current.has(pageNum)) return;
            loadingPagesRef.current.add(pageNum);

            try {
                const page = await pdf.getPage(pageNum);
                if (cancelled) return;

                const baseViewport = page.getViewport({ scale: 1 });
                // Use stableWidth to ensure all pages are patterned to the same width
                const finalScale = (stableWidth / baseViewport.width) * zoom;
                const viewport = page.getViewport({ scale: finalScale });

                // Set exact dimensions to prevent layout shifts
                pageHost.style.height = `${viewport.height}px`;
                pageHost.style.width = `${viewport.width}px`;

                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                const dpr = window.devicePixelRatio || 1;
                canvas.width = Math.floor(viewport.width * dpr);
                canvas.height = Math.floor(viewport.height * dpr);
                canvas.style.width = `${viewport.width}px`;
                canvas.style.height = `${viewport.height}px`;
                context.setTransform(dpr, 0, 0, dpr, 0, 0);

                await page.render({ canvasContext: context, viewport }).promise;
                if (cancelled) return;

                // Clear placeholder content and add canvas
                pageHost.innerHTML = '';
                pageHost.classList.remove('skeleton');
                pageHost.appendChild(canvas);

                // 1. Render Text Layer (for selection)
                const textContent = await page.getTextContent();
                if (textContent && !cancelled) {
                    const textLayer = document.createElement('div');
                    textLayer.className = 'text-layer';
                    textLayer.style.width = `${viewport.width}px`;
                    textLayer.style.height = `${viewport.height}px`;
                    
                    pageHost.appendChild(textLayer);
                    await window.pdfjsLib.renderTextLayer({
                        textContentSource: textContent,
                        container: textLayer,
                        viewport: viewport
                    }).promise;
                }

                // 2. Render Annotation Layer (for links)
                const annotations = await page.getAnnotations();
                if (annotations.length > 0 && !cancelled) {
                    const overlay = document.createElement('div');
                    overlay.className = 'annotation-layer';
                    overlay.style.width = `${viewport.width}px`;
                    overlay.style.height = `${viewport.height}px`;
                    
                    for (const ann of annotations) {
                        const url = ann.url || ann.unsafeUrl;
                        if (ann.subtype === 'Link' && url) {
                            const rect = window.pdfjsLib.Util.normalizeRect(viewport.convertToViewportRectangle(ann.rect));
                            const [x1, y1, x2, y2] = rect;
                            const link = document.createElement('a');
                            link.href = url;
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            link.style.position = 'absolute';
                            link.style.left = `${x1}px`;
                            link.style.top = `${y1}px`;
                            link.style.width = `${x2 - x1}px`;
                            link.style.height = `${y2 - y1}px`;
                            link.style.cursor = 'pointer';
                            link.style.pointerEvents = 'auto';
                            overlay.appendChild(link);
                        }
                    }
                    pageHost.appendChild(overlay);
                }

                renderedPagesRef.current.add(pageNum);
            } catch (err) {
                console.error(`Error rendering page ${pageNum}:`, err);
            } finally {
                loadingPagesRef.current.delete(pageNum);
            }
        }

        async function init() {
            container.innerHTML = '';
            
            // Fetch first page to get initial dimensions for placeholders
            const firstPage = await pdf.getPage(1);
            if (cancelled) return;

            const baseViewport = firstPage.getViewport({ scale: 1 });
            
            // Determine a stable width once. 
            // We use the container width if available, otherwise fall back to first page width.
            stableWidth = container.clientWidth || baseViewport.width;
            
            const finalScale = (stableWidth / baseViewport.width) * zoom;
            const viewport = firstPage.getViewport({ scale: finalScale });
            const estimatedHeight = viewport.height;
            const targetWidth = viewport.width;

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const pageHost = document.createElement('div');
                pageHost.className = 'page-host skeleton';
                pageHost.setAttribute('data-page', pageNum);
                pageHost.style.position = 'relative';
                pageHost.style.marginBottom = '20px';
                pageHost.style.display = 'flex';
                pageHost.style.justifyContent = 'center';
                pageHost.style.height = `${estimatedHeight}px`;
                pageHost.style.width = `${targetWidth}px`; // Pattern to exact width from the start
                
                container.appendChild(pageHost);
                visibilityObserver.observe(pageHost);
                lazyObserver.observe(pageHost);
            }

            // Immediately trigger load for the first page
            const firstHost = container.querySelector(`[data-page="1"]`);
            if (firstHost) {
                renderPage(1, firstHost);
            }
        }

        init();

        return () => {
            cancelled = true;
            visibilityObserver.disconnect();
            lazyObserver.disconnect();
        };
    }, [pdf, zoom]);

    if (loading) {
        return (
            <div className="pdf-viewport">
                {[1, 2, 3].map(i => (
                    <div 
                        key={i} 
                        className="page-host skeleton mt-3" 
                        style={{ height: '800px', width: '100%', marginBottom: '20px' }} 
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="pdf-viewport" ref={containerRef} onContextMenu={(e) => e.preventDefault()} />
    );
}

